import { useEffect, useRef, useState } from 'react'
import { Popup, type Map as MlMap, type MapLayerMouseEvent } from 'maplibre-gl'
import { CC, TONE_HEX } from '../cc/tokens'
import type { BasemapMode } from './basemaps'
import {
  createStandaloneMap, escapeHtml, fitTo, isLngLat, setGeoJson, standaloneStyle, styleGate,
  type LngLat, type StyleGate,
} from './standaloneMap'

/**
 * Where alerts fire: one bubble per `/api/reports/alerts/by-location` row (place + zone +
 * lat/lon bin centroid), sized by alert count and coloured by how much of it is critical.
 *
 * Same self-owned instance recipe as TripMap.tsx (see standaloneMap.ts). Size and colour
 * are precomputed into feature properties rather than written as data-driven expressions
 * over the raw counts, because the scale depends on the max of the whole set -- a
 * 'interpolate' over `alerts` alone would make a quiet week's hot spot look as big as a
 * bad week's.
 */

export interface LocationMapRow {
  place: string
  zone: string | null
  alerts: number
  critical: number
  lat: number | null
  lon: number | null
  top_kind: string | null
}

export interface LocationMapProps {
  rows: LocationMapRow[]
  onSelect?: (row: LocationMapRow) => void
  basemap?: BasemapMode
  /** Changing it re-fits the camera over every bubble; without one the camera fits
   *  whenever `rows` changes. */
  fitKey?: string
  className?: string
}

export const SRC_LOCATION_BUBBLES = 'location-bubbles'
export const LYR_LOCATION_BUBBLES = 'location-bubbles-circle'

const DEFAULT_CENTER: LngLat = [75.1, 21.4]
const MIN_RADIUS = 6
const MAX_RADIUS = 26

function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

function mix(a: string, b: string, t: number): string {
  const [ar, ag, ab] = hexToRgb(a)
  const [br, bg, bb] = hexToRgb(b)
  const c = (x: number, y: number): string => Math.round(x + (y - x) * t).toString(16).padStart(2, '0')
  return `#${c(ar, br)}${c(ag, bg)}${c(ab, bb)}`.toUpperCase()
}

/** 0 → info blue, 0.5 → warn amber, 1 → danger rose: the console's own severity triple,
 *  so a bubble that is mostly critical is the same red the CRITICAL pill is. */
export function criticalShareColor(share: number): string {
  const s = Math.min(1, Math.max(0, Number.isFinite(share) ? share : 0))
  return s <= 0.5
    ? mix(TONE_HEX.info, TONE_HEX.warn, s / 0.5)
    : mix(TONE_HEX.warn, TONE_HEX.danger, (s - 0.5) / 0.5)
}

/** Area-proportional (sqrt) sizing against the set's own max, so twice the alerts reads
 *  as twice the ink rather than four times. Rows without a centroid are dropped -- same
 *  "nowhere honest to plot it" rule as the alert layers. */
export function locationBubbleFeatures(rows: readonly LocationMapRow[]): GeoJSON.FeatureCollection<GeoJSON.Point> {
  const max = rows.reduce((m, r) => Math.max(m, r.alerts), 0)
  return {
    type: 'FeatureCollection',
    features: rows
      .map((r, idx) => ({ r, idx }))
      .filter(({ r }) => isLngLat(r.lon, r.lat) && r.alerts > 0)
      .map(({ r, idx }) => ({
        type: 'Feature',
        id: idx,
        geometry: { type: 'Point', coordinates: [r.lon as number, r.lat as number] },
        properties: {
          idx,
          place: r.place,
          zone: r.zone,
          alerts: r.alerts,
          critical: r.critical,
          top_kind: r.top_kind,
          radius: MIN_RADIUS + (MAX_RADIUS - MIN_RADIUS) * Math.sqrt(max > 0 ? r.alerts / max : 0),
          color: criticalShareColor(r.alerts > 0 ? r.critical / r.alerts : 0),
        },
      })),
  }
}

function bubblePopupHtml(p: Record<string, unknown>): string {
  const zone = p.zone ? ` · ${escapeHtml(String(p.zone))}` : ''
  const top = p.top_kind ? ` · ${escapeHtml(String(p.top_kind))}` : ''
  return `<div style="font:12px/1.45 ui-sans-serif,system-ui,sans-serif;color:${CC.ink};max-width:240px">`
    + `<div style="font-weight:600">${escapeHtml(String(p.place ?? ''))}${zone}</div>`
    + `<div style="font-family:ui-monospace,monospace;font-size:11px;color:${CC.muted}">`
    + `${Number(p.alerts ?? 0)} alerts · ${Number(p.critical ?? 0)} critical${top}</div>`
    + `</div>`
}

export function LocationMap({
  rows, onSelect, basemap = 'osm', fitKey, className = 'h-full w-full',
}: LocationMapProps): JSX.Element {
  const container = useRef<HTMLDivElement>(null)
  const mapRef = useRef<MlMap | null>(null)
  const gateRef = useRef<StyleGate | null>(null)
  const rowsRef = useRef(rows)
  rowsRef.current = rows
  const onSelectRef = useRef(onSelect)
  onSelectRef.current = onSelect
  const [styleEpoch, setStyleEpoch] = useState(0)
  const appliedBasemap = useRef(basemap)
  // Which data the camera was last fitted to. Belongs to the map INSTANCE, so it is
  // cleared when that instance is removed: under StrictMode (dev) the construction effect
  // runs mount -> cleanup -> mount, the first fit queues on the map the cleanup removes,
  // and a surviving signature made the second, real map skip its fit -- the drawer opened
  // on the default centre with the trip and its alerts off-screen.
  const lastFit = useRef<string | null>(null)

  useEffect(() => {
    if (!container.current || mapRef.current) return
    const map = createStandaloneMap(container.current, appliedBasemap.current, DEFAULT_CENTER, 8)
    mapRef.current = map
    const gate = styleGate(map)
    gateRef.current = gate

    const hover = new Popup({ closeButton: false, closeOnClick: false, offset: 10, maxWidth: '260px' })
    map.on('mouseenter', LYR_LOCATION_BUBBLES, (e: MapLayerMouseEvent) => {
      const f = e.features?.[0]
      if (!f || f.geometry.type !== 'Point') return
      map.getCanvas().style.cursor = 'pointer'
      hover.setLngLat(f.geometry.coordinates as LngLat).setHTML(bubblePopupHtml(f.properties ?? {})).addTo(map)
    })
    map.on('mouseleave', LYR_LOCATION_BUBBLES, () => {
      map.getCanvas().style.cursor = ''
      hover.remove()
    })
    map.on('click', LYR_LOCATION_BUBBLES, (e: MapLayerMouseEvent) => {
      const idx = e.features?.[0]?.properties?.idx
      const row = typeof idx === 'number' ? rowsRef.current[idx] : undefined
      if (row) onSelectRef.current?.(row)
    })

    return () => {
      hover.remove()
      map.remove()
      mapRef.current = null
      gateRef.current = null
      lastFit.current = null
    }
  }, [])

  useEffect(() => {
    const map = mapRef.current
    const gate = gateRef.current
    if (!map || !gate || appliedBasemap.current === basemap) return
    appliedBasemap.current = basemap
    gate.rearm()
    map.setStyle(standaloneStyle(basemap))
    setStyleEpoch((n) => n + 1)
  }, [basemap])

  useEffect(() => {
    const map = mapRef.current
    const gate = gateRef.current
    if (!map || !gate) return
    gate.run('bubbles', () => {
      if (!map.getSource(SRC_LOCATION_BUBBLES)) {
        map.addSource(SRC_LOCATION_BUBBLES, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
      }
      if (!map.getLayer(LYR_LOCATION_BUBBLES)) {
        map.addLayer({
          id: LYR_LOCATION_BUBBLES,
          type: 'circle',
          source: SRC_LOCATION_BUBBLES,
          paint: {
            'circle-radius': ['get', 'radius'],
            'circle-color': ['get', 'color'],
            'circle-opacity': 0.72,
            'circle-stroke-color': '#FFFFFF',
            'circle-stroke-width': 1.5,
          },
        })
      }
      setGeoJson(map, SRC_LOCATION_BUBBLES, locationBubbleFeatures(rows))
    })
  }, [rows, styleEpoch])

  useEffect(() => {
    const map = mapRef.current
    const gate = gateRef.current
    if (!map || !gate) return
    const sig = fitKey ?? `auto|${rows.length}`
    if (lastFit.current === sig) return
    const coords = rows.filter((r) => isLngLat(r.lon, r.lat)).map((r): LngLat => [r.lon as number, r.lat as number])
    if (!coords.length) return
    lastFit.current = sig
    gate.run('fit', () => fitTo(map, coords, 40))
  }, [rows, fitKey])

  return <div ref={container} className={className} data-testid="location-map" />
}
